/**
 * Okta Authentication - Proxy Implementation
 * Uses a backend proxy to avoid CORS issues
 * Based on mobile app pattern from fmc-digital-mobile-app/api/auth/src/okta/okta.ts
 */
import axios from "axios";
import { exchangeSessionTokenForOAuthTokens } from "./exchangeSessionToken";
// Configurable Okta base URL (set from consuming app)
let oktaBaseUrl = undefined;
/**
 * Set the Okta base URL from the consuming app (e.g., from config.json)
 */
export function setOktaBaseUrl(url) {
    oktaBaseUrl = url;
    console.log("🔑 Okta base URL set via config setter:", oktaBaseUrl);
}
// Store the proxy configuration
let shouldUseProxy = true;
/**
 * Set whether to use proxy for Okta API calls
 * @param useProxy - true to use proxy (for CORS avoidance), false for direct calls
 */
export function setOktaProxyConfig(useProxy) {
    shouldUseProxy = useProxy;
    console.log("🔑 Okta proxy configuration set:", { useProxy });
}
// Use proxy path in development, direct domain in production, or injected config
const getOktaBaseUrl = () => {
    if (oktaBaseUrl) {
        return oktaBaseUrl;
    }
    // If explicitly disabled, don't use proxy
    if (!shouldUseProxy) {
        return "https://identity-dev.freedommortgage.com";
    }
    // Use proxy only on localhost to avoid CORS
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
        return "/okta-api"; // Vite proxy will forward to Okta domain
    }
    // Non-localhost environments - use direct domain
    return "https://identity-dev.freedommortgage.com";
};
/**
 * Sign in with username and password using Okta API directly
 * Web login doesn't require device token (mobile-only)
 */
export async function signInWithProxy(credentials) {
    const baseUrl = getOktaBaseUrl();
    console.log("🔑 Okta proxy signIn called with:", {
        username: credentials.username,
        baseUrl,
    });
    try {
        console.log("🔑 Making request to:", `${baseUrl}/api/v1/authn`);
        const response = await axios.post(`${baseUrl}/api/v1/authn`, {
            username: credentials.username,
            password: credentials.password,
            options: {
                multiOptionalFactorEnroll: false,
                warnBeforePasswordExpired: false,
            },
        }, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
        });
        console.log("🔑 Okta response status:", response.data.status);
        if (response.data.status === "LOCKED_OUT") {
            return {
                success: false,
                status: "LOCKED_OUT",
            };
        }
        if (response.data.status === "SUCCESS") {
            // Session token received, exchange for tokens
            // For web, we'll store the session token and use it later
            // In a full implementation, you'd exchange this for OAuth tokens
            const sessionToken = response.data.sessionToken;
            // Extract user information from response to avoid CORS calls later
            const userData = response.data._embedded?.user;
            // Exchange session token for OAuth tokens
            const tokens = await exchangeSessionTokenForOAuthTokens(sessionToken, userData);
            return {
                success: true,
                tokens,
            };
        }
        if (response.data.status === "MFA_REQUIRED") {
            // Extract MFA factors with verify links
            const factors = response.data._embedded?.factors?.map((factor) => ({
                id: factor.id,
                factorType: factor.factorType,
                provider: factor.provider,
                status: "ACTIVE",
                displayName: factor.vendorName,
                link: factor._links?.verify?.href, // Verify link for triggering OTP
                profile: {
                    phoneNumber: factor.profile?.phoneNumber,
                    email: factor.profile?.email,
                },
            })) ?? [];
            return {
                success: false,
                mfaState: {
                    stateToken: response.data.stateToken,
                    factors,
                    status: "MFA_REQUIRED",
                },
            };
        }
        throw new Error(`Unexpected status: ${response.data.status}`);
    }
    catch (error) {
        console.error("🔑 Okta proxy sign in failed:", error);
        // Enhanced error handling for Okta API errors
        if (axios.isAxiosError(error) && error.response?.data) {
            const oktaError = error.response.data;
            console.error("🔑 Okta API Error Details:", {
                status: error.response.status,
                errorCode: oktaError.errorCode,
                errorSummary: oktaError.errorSummary,
                errorCauses: oktaError.errorCauses,
            });
            // Map common Okta error codes to user-friendly messages
            let userMessage = oktaError.errorSummary ?? "Authentication failed";
            if (oktaError.errorCode === "E0000004") {
                userMessage = "Invalid username or password. Please check your credentials and try again.";
            }
            else if (oktaError.errorCode === "E0000011") {
                userMessage = "Invalid token provided. Please try logging in again.";
            }
            else if (oktaError.errorCode === "E0000047") {
                userMessage = "Your account has been locked. Please contact support.";
            }
            else if (oktaError.errorCode === "E0000069") {
                userMessage = "Too many failed login attempts. Your account has been temporarily locked.";
            }
            const enhancedError = new Error(userMessage);
            enhancedError.errorCode = oktaError.errorCode;
            enhancedError.status = error.response.status;
            throw enhancedError;
        }
        throw error;
    }
}
/**
 * Send initial passcode for login MFA (trigger OTP)
 * Uses the factor's verify link from login response
 */
export async function sendInitialPasscode(link, stateToken) {
    try {
        console.log("🔑 Sending initial passcode - Original link:", link);
        console.log("🔑 Window location hostname:", typeof window !== "undefined" ? window.location.hostname : "undefined");
        // Always use proxy path in development (localhost)
        let requestUrl = link;
        if (typeof window !== "undefined") {
            const isLocalhost = window.location.hostname === "localhost" ||
                window.location.hostname === "127.0.0.1";
            console.log("🔑 Is localhost:", isLocalhost);
            if (isLocalhost) {
                // Replace the full Okta domain with the proxy path
                requestUrl = link.replace(/https?:\/\/identity-dev\.freedommortgage\.com/, "/okta-api");
                console.log("🔑 Converted URL for proxy:", requestUrl);
            }
        }
        console.log("🔑 Final request URL:", requestUrl);
        const response = await axios.post(requestUrl, { stateToken }, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
        });
        console.log("🔑 Initial passcode sent successfully");
        console.log("🔑 Response status:", response.data.status);
        console.log("🔑 New stateToken received:", !!response.data.stateToken);
        // Return the new stateToken from the response
        return {
            stateToken: response.data.stateToken ?? stateToken, // Fallback to original if not provided
        };
    }
    catch (error) {
        console.error("🔑 Failed to send initial passcode:", error);
        console.log("🔑 Full error object:", JSON.stringify(error, null, 2));
        // Extract meaningful error message from various response formats
        let errorMessage = "Failed to send passcode";
        let errorCode;
        let status;
        if (axios.isAxiosError(error) && error.response?.data) {
            const data = error.response.data;
            console.log("🔑 API error response data:", data);
            // Try different error message fields that Okta API might use
            errorMessage =
                data.errorSummary ??
                    (data.errorCauses?.[0]?.errorSummary) ??
                    data.message ??
                    data.error ??
                    errorMessage;
            errorCode = data.errorCode;
            status = error.response.status;
            // Map common HTTP status codes to user-friendly messages
            if (status === 403) {
                errorMessage = errorMessage ?? "You do not have permission to perform this action";
            }
            else if (status === 429) {
                errorMessage = "Too many attempts. Please try again later.";
            }
            else if (status === 401) {
                errorMessage = "Your session has expired. Please log in again.";
            }
        }
        console.log("🔑 Extracted error:", { errorMessage, errorCode, status });
        // Create enhanced error with additional properties
        const enhancedError = new Error(errorMessage);
        enhancedError.errorCode = errorCode;
        enhancedError.status = status;
        enhancedError.originalError = error;
        throw enhancedError;
    }
}
/**
 * Confirm passcode for login MFA (verify OTP)
 * Uses the factor's verify link from login response
 * Matching mobile app pattern
 */
export async function confirmPasscode(link, stateToken, passCode) {
    // Helper: convert Okta URL for localhost
    const convertOktaUrl = (link, isBrowser, hostname) => {
        if (isBrowser && (hostname === "localhost" || hostname === "127.0.0.1")) {
            return link.replace(/https?:\/\/identity-dev\.freedommortgage\.com/, "/okta-api");
        }
        return link;
    };
    // Helper: handle Okta API errors
    const handleOktaApiError = (error) => {
        if (axios.isAxiosError(error) && error.response?.data) {
            const oktaError = error.response.data;
            console.log("🔑 Okta error object:", oktaError);
            if (oktaError.errorCode) {
                const errorMessage = oktaError.errorSummary ?? oktaError.errorCauses?.[0]?.errorSummary ?? "Invalid passcode";
                const customError = new Error(errorMessage);
                customError.errorCode = oktaError.errorCode;
                console.log("🔑 Throwing custom error:", { message: errorMessage, errorCode: oktaError.errorCode });
                throw customError;
            }
        }
    };
    // Force detailed logging
    const isBrowser = typeof window !== "undefined";
    const hostname = isBrowser ? window.location.hostname : "server";
    console.log("=== CONFIRM PASSCODE DEBUG ===");
    console.log("Original link:", link);
    console.log("Is browser environment:", isBrowser);
    console.log("Hostname:", hostname);
    try {
        let requestUrl = convertOktaUrl(link, isBrowser, hostname);
        console.log("Final request URL:", requestUrl);
        console.log("=== END DEBUG ===");
        const response = await axios.post(requestUrl, { stateToken, passCode }, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
        });
        console.log("🔑 Passcode API response:", {
            status: response.status,
            dataStatus: response.data.status,
            hasSessionToken: !!response.data.sessionToken,
            fullResponse: response.data,
        });
        if (response.data.status === "LOCKED_OUT") {
            throw new Error("Account locked out");
        }
        if (response.data.status === "SUCCESS") {
            const sessionToken = response.data.sessionToken;
            console.log("🔑 [confirmPasscode] MFA SUCCESS - sessionToken received:", {
                sessionTokenLength: sessionToken?.length,
                sessionTokenPreview: sessionToken?.substring(0, 20) + '...',
            });
            // Extract user information from response to avoid CORS calls later
            const userData = response.data._embedded?.user;
            // Exchange session token for OAuth tokens and store in sessionStorage
            // This ensures the session token is available for redirect logic in LoginPage
            // Must match the same flow as signInWithProxy() to ensure MFA users can redirect properly
            try {
                console.log("🔑 [confirmPasscode] Calling exchangeSessionTokenForOAuthTokens...");
                await exchangeSessionTokenForOAuthTokens(sessionToken, userData);
                console.log("🔑 [confirmPasscode] sessionToken stored successfully");
                // Verify it was stored
                const storedToken = sessionStorage.getItem("okta_session_token");
                console.log("🔑 [confirmPasscode] Verification - stored token exists:", !!storedToken);
            }
            catch (storageError) {
                console.error("🔑 [confirmPasscode] FAILED to store sessionToken:", storageError);
                throw storageError;
            }
            const accessToken = await authorizeSessionToken(sessionToken);
            return {
                ...response.data,
                sessionToken,
                accessToken,
            };
        }
        // If we reach here, it's an unexpected status - throw error
        console.error("🔑 Unexpected status from Okta:", response.data.status);
        throw new Error(`Unexpected response status: ${response.data.status}`);
    }
    catch (error) {
        console.error("🔑 Failed to confirm passcode:", error);
        console.log("🔑 Error details:", {
            isAxiosError: axios.isAxiosError(error),
            hasResponseData: !!(axios.isAxiosError(error) && error.response?.data),
            responseData: axios.isAxiosError(error) ? error.response?.data : null,
        });
        handleOktaApiError(error);
        throw error;
    }
}
/**
 * Authorize session token to get access token
 * Matching mobile app pattern
 */
async function authorizeSessionToken(sessionToken) {
    // For web, just return the session token as access token
    console.log("🔑 Using session token as access token");
    return sessionToken;
}
/**
 * Verify MFA code using Okta API
 */
export async function verifyMFAWithProxy(request) {
    const baseUrl = getOktaBaseUrl();
    console.log("🔑 Okta proxy verifyMFA called");
    try {
        // Find the factor's verify link
        // In a full implementation, you'd need to store the factor link from the initial MFA response
        // For now, we'll construct it based on the factor ID
        const response = await axios.post(`${baseUrl}/api/v1/authn/factors/${request.factorId}/verify`, {
            stateToken: request.stateToken,
            passCode: request.passCode,
        }, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        console.log("🔑 MFA verify response status:", response.data.status);
        if (response.data.status === "SUCCESS") {
            const sessionToken = response.data.sessionToken;
            return {
                success: true,
                tokens: {
                    accessToken: sessionToken,
                    idToken: sessionToken,
                    expiresAt: Math.floor(Date.now() / 1000) + 3600,
                    tokenType: "Bearer",
                },
            };
        }
        if (response.data.status === "MFA_CHALLENGE") {
            return {
                success: false,
                mfaState: {
                    stateToken: response.data.stateToken,
                    factors: [],
                    status: "MFA_CHALLENGE",
                },
            };
        }
        throw new Error(`Unexpected status: ${response.data.status}`);
    }
    catch (error) {
        console.error("🔑 Okta proxy MFA verify failed:", error);
        // Extract stateToken from error response to allow retry
        if (axios.isAxiosError(error) && error.response?.data) {
            const oktaError = error.response.data;
            console.log("🔑 Okta error response:", oktaError);
            // Preserve stateToken for retry even on error
            if (oktaError.stateToken) {
                const errorMessage = oktaError.errorSummary ?? "Verification failed";
                const customError = new Error(errorMessage);
                customError.errorCode = oktaError.errorCode;
                customError.stateToken = oktaError.stateToken;
                console.log("🔑 Preserved stateToken for retry:", {
                    errorCode: customError.errorCode,
                    hasStateToken: !!customError.stateToken,
                });
                throw customError;
            }
        }
        throw error;
    }
}
/**
 * Select and send MFA factor challenge
 */
export async function selectMFAFactorWithProxy(stateToken, factorId) {
    const baseUrl = getOktaBaseUrl();
    console.log("🔑 Okta proxy selectMFAFactor called");
    try {
        const response = await axios.post(`${baseUrl}/api/v1/authn/factors/${factorId}/verify`, {
            stateToken,
        }, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        console.log("🔑 MFA select response status:", response.data.status);
        return {
            stateToken: response.data.stateToken,
            factors: [],
            status: "MFA_CHALLENGE",
        };
    }
    catch (error) {
        console.error("🔑 Okta proxy select MFA factor failed:", error);
        throw error;
    }
}
//# sourceMappingURL=okta.proxy.js.map